import React, { useState } from 'react';
import type { QuestionConfig, SurveyDetail, SurveyMode } from '../types';
import { SurveyHubHero } from './SurveyHubHero';
import '../survey-hub.css';

interface Props {
  initialTitle?: string;
  initialAudience?: string;
  initialLanguage?: string;
  initialMode?: SurveyMode;
  initialQuestions?: QuestionConfig[];
  onSaveDraft: (data: {
    title: string;
    description: string;
    audience: string;
    language: string;
    mode: SurveyMode;
    questions: QuestionConfig[];
  }) => Promise<SurveyDetail | void>;
  onOpenPreview: (data: {
    title: string;
    description: string;
    audience: string;
    language: string;
    mode: SurveyMode;
    questions: QuestionConfig[];
  }) => void;
  onBack: () => void;
}

const TYPE_LABELS: Record<string, string> = {
  text: 'Открытый текст',
  single: 'Одиночный выбор',
  multi: 'Множественный выбор',
  scale: 'Шкала',
  matrix: 'Матрица',
  date: 'Дата',
};

const DEFAULT_QUESTIONS: QuestionConfig[] = [
  {
    order_index: 1,
    question_type: 'single',
    title: 'Насколько понятен индивидуальный план?',
    prompt: 'Оцените понимание структуры плана',
    is_required: true,
    options: ['Полностью', 'Частично', 'Пока непонятен'],
  },
  {
    order_index: 2,
    question_type: 'multi',
    title: 'Какие направления поддержки вам нужны?',
    prompt: 'Выберите все актуальные направления',
    is_required: true,
    options: ['Публикации', 'Стажировка', 'Практика'],
  },
  {
    order_index: 3,
    question_type: 'scale',
    title: 'Оцените доступность научных консультаций по шкале 1–5',
    prompt: 'Регулярность и качество консультаций',
    is_required: true,
    scale_min: 1,
    scale_max: 5,
  },
  {
    order_index: 4,
    question_type: 'text',
    title: 'Какую поддержку вы хотели бы получить в сентябре?',
    prompt: 'Свободный ответ',
    is_required: false,
  },
  {
    order_index: 5,
    question_type: 'matrix',
    title: 'Оцените направления поддержки: публикации / стажировка / практика',
    prompt: 'Матрица оценок',
    is_required: true,
    matrix_rows: ['Публикации', 'Стажировка', 'Практика'],
    matrix_cols: ['1', '2', '3', '4', '5'],
  },
  {
    order_index: 6,
    question_type: 'date',
    title: 'Дата последней встречи с научным руководителем',
    prompt: 'Дата',
    is_required: true,
  },
];

/** Survey builder — template builder(). */
export const SurveyBuilderView: React.FC<Props> = ({
  initialTitle = 'Академическая адаптация докторантов 1 курса',
  initialAudience = 'Докторанты · 1 курс',
  initialLanguage = 'RU',
  initialMode = 'ANONYMOUS',
  initialQuestions,
  onSaveDraft,
  onOpenPreview,
  onBack,
}) => {
  const [title, setTitle] = useState(initialTitle);
  const [audience, setAudience] = useState(initialAudience);
  const [language, setLanguage] = useState(initialLanguage);
  const [mode, setMode] = useState<SurveyMode>(initialMode);
  const [questions, setQuestions] = useState<QuestionConfig[]>(
    initialQuestions?.length ? initialQuestions : DEFAULT_QUESTIONS,
  );
  const [saving, setSaving] = useState(false);

  const payload = () => ({
    title,
    description: 'Исследовательский опрос',
    audience,
    language,
    mode,
    questions,
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      await onSaveDraft(payload());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="survey-hub-content">
      <SurveyHubHero
        title="Конструктор опроса"
        description="Редактирование вопросов перед публикацией. Предпросмотр показывает именно то, что увидит респондент."
      />

      <section className="survey-hub-panel">
        <header>
          <div className="grow">
            <h3>Параметры</h3>
            <p>Черновик сохраняется при возврате и закрытии.</p>
          </div>
          <span className="survey-hub-chip">Черновик</span>
        </header>
        <div className="survey-hub-form">
          <div className="survey-hub-field">
            <label>Название *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="survey-hub-field">
            <label>Аудитория *</label>
            <select value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option>Докторанты · 1 курс</option>
              <option>Все докторанты</option>
              <option>Группа 8D01717-01</option>
            </select>
          </div>
          <div className="survey-hub-field">
            <label>Язык</label>
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option>RU</option>
              <option>KZ</option>
              <option>EN</option>
            </select>
          </div>
          <div className="survey-hub-field">
            <label>Идентификация</label>
            <select value={mode} onChange={(e) => setMode(e.target.value as SurveyMode)}>
              <option value="ANONYMOUS">Анонимно · без e-mail</option>
              <option value="VERIFIED">С подтверждением · e-mail + код</option>
            </select>
          </div>
        </div>
      </section>

      <section className="survey-hub-panel">
        <header>
          <div className="grow">
            <h3>Вопросы</h3>
            <p>Типы можно менять до предпросмотра.</p>
          </div>
          <button
            type="button"
            className="survey-hub-btn"
            onClick={() =>
              setQuestions((prev) => [
                ...prev,
                {
                  order_index: prev.length + 1,
                  question_type: 'text',
                  title: 'Новый вопрос',
                  prompt: '',
                  is_required: false,
                },
              ])
            }
          >
            + Добавить вопрос
          </button>
        </header>

        {questions.map((q, i) => (
          <div key={`${q.order_index}-${i}`} className="survey-hub-question">
            <div className="survey-hub-form">
              <div className="survey-hub-field">
                <label>Тип</label>
                <select
                  value={q.question_type}
                  onChange={(e) => {
                    const next = [...questions];
                    next[i] = { ...q, question_type: e.target.value as QuestionConfig['question_type'] };
                    setQuestions(next);
                  }}
                >
                  {Object.entries(TYPE_LABELS).map(([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="survey-hub-field">
                <label>Обязательность</label>
                <select
                  value={q.is_required ? '1' : '0'}
                  onChange={(e) => {
                    const next = [...questions];
                    next[i] = { ...q, is_required: e.target.value === '1' };
                    setQuestions(next);
                  }}
                >
                  <option value="1">Обязательный</option>
                  <option value="0">Необязательный</option>
                </select>
              </div>
              <div className="survey-hub-field full">
                <label>Вопрос {i + 1}</label>
                <input
                  value={q.title}
                  onChange={(e) => {
                    const next = [...questions];
                    next[i] = { ...q, title: e.target.value };
                    setQuestions(next);
                  }}
                />
              </div>
            </div>
          </div>
        ))}

        <div className="survey-hub-actions">
          <button type="button" className="survey-hub-btn" onClick={onBack}>
            ← Назад
          </button>
          <button type="button" className="survey-hub-btn" onClick={saveDraft} disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить черновик'}
          </button>
          <button
            type="button"
            className="survey-hub-btn primary"
            onClick={() => onOpenPreview(payload())}
          >
            Точный предпросмотр →
          </button>
        </div>
      </section>
    </div>
  );
};
